import moment from 'moment';

import { MessageBundle } from '@amzn/arb-tools';
import { RelationToAmazon } from '@amzn/coral_com-amazon-asxcustomerprofileservice';

import { FormStrings } from 'src/constants';
import { t } from 'src/helpers';

export enum INTAKE_FORM_KEYS {
  NAME = 'name',
  EMAIL = 'email',
  ORGANIZATION_NAME = 'organizationName',
  ORGANIZATION_RELATIONSHIP = 'organizationRelationship',
  NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION = 'question1',
  SUBQUESTION_1 = 'subquestion1',
  MEASURE_AND_REPORT_CRITERIA_QUESTION = 'question2',
  DECARBONIZATION_COMMITMENT_CRITERIA_QUESTION = 'question3',
  SUBQUESTION_3 = 'subquestion3',
  PUBLIC_COMMITMENTS = 'publicCommitments',
  PUBLIC_COMMITMENTS_URL = 'publicCommitmentsUrl',
  SCIENCE_BASED_TARGETS = 'scienceBasedTargets',
  SBTI_TARGETS_APPROVED = 'sbtiTargetsApproved',
  COUNTRY_CODE = 'countryCode',
  FIRST_ADDRESS_LINE = 'firstAddressLine',
  SECOND_ADDRESS_LINE = 'secondAddressLine',
  POSTAL_CODE = 'postalCode',
  CITY = 'city',
  STATE_OR_REGION = 'stateOrRegion',
  JOB_TITLE = 'jobTitle',
  ORGANIZATION_SIZE = 'organizationSize',
  ORGANIZATION_WEBSITE = 'organizationWebsite',
  PHONE_NUMBER = 'phoneNumber',
}

export type OrganizationInformationFormValues = {
  [INTAKE_FORM_KEYS.NAME]: string;
  [INTAKE_FORM_KEYS.EMAIL]: string;
  [INTAKE_FORM_KEYS.ORGANIZATION_NAME]: string;
  [INTAKE_FORM_KEYS.ORGANIZATION_RELATIONSHIP]: RelationToAmazon[];
  [INTAKE_FORM_KEYS.COUNTRY_CODE]: string;
};

export const SUPPORTED_COUNTRIES: { value: string; label: string }[] = [
  { value: 'US', label: '🇺🇸 United States' },
  { value: 'UK', label: '🇬🇧 United Kingdom' },
];

export type OrganizationCommitmentsFormValues = {
  [INTAKE_FORM_KEYS.NET_ZERO_CARBON_TARGET_CRITERIA_QUESTION]: string;
  [INTAKE_FORM_KEYS.SUBQUESTION_1]: string;
  [INTAKE_FORM_KEYS.MEASURE_AND_REPORT_CRITERIA_QUESTION]: string;
  [INTAKE_FORM_KEYS.DECARBONIZATION_COMMITMENT_CRITERIA_QUESTION]: string;
  [INTAKE_FORM_KEYS.SUBQUESTION_3]: string;
};

export type BillingAddressInformationFormValues = {
  [INTAKE_FORM_KEYS.COUNTRY_CODE]: string;
  [INTAKE_FORM_KEYS.PHONE_NUMBER]: string;
  [INTAKE_FORM_KEYS.FIRST_ADDRESS_LINE]: string;
  [INTAKE_FORM_KEYS.SECOND_ADDRESS_LINE]?: string;
  [INTAKE_FORM_KEYS.POSTAL_CODE]: string;
  [INTAKE_FORM_KEYS.CITY]: string;
  [INTAKE_FORM_KEYS.STATE_OR_REGION]: string;
};

export const informationFields: (keyof OrganizationInformationFormValues)[] = [
  INTAKE_FORM_KEYS.NAME,
  INTAKE_FORM_KEYS.EMAIL,
  INTAKE_FORM_KEYS.ORGANIZATION_NAME,
  INTAKE_FORM_KEYS.ORGANIZATION_RELATIONSHIP,
  INTAKE_FORM_KEYS.COUNTRY_CODE,
];

export const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const yearRegex = /^\d{4}$/;
export const monthYearRegex = /^(0[1-9]|1[0-2])\/\d{4}$/;

/**
 * Allowlist regex for free-form text fields (e.g., organization name).
 * Allows all unicode letters, numbers, punctuation, separators, and some additional specific characters.
 */
export const freeFormTextRegex = /^[\p{Letter}\p{Number}\p{Punctuation}\p{Separator}.,!?'"()-/:@&=+$#%_;*]*$/u;

/**
 * Allowlist regex for name fields (e.g., full name).
 * Requires at least one non-whitespace character. Allows unicode letters, numbers, punctuation, and separators.
 */
export const nameRegex = /^(?=.*\S)[\p{Letter}\p{Number}\p{Punctuation}\p{Separator}]+$/u;

export const shortInputMaxLength = 300;
export const longInputMaxLength = 1000;

export const DEFAULT_COUNTRY = 'US';

/** Enum for valid question response values */
export enum QUESTION_RESPONSE {
  YES = 'Yes',
  NO = 'No',
  IN_PROCESS = 'In process',
}

/** Valid responses for Yes/No questions (question1, question2) */
export const VALID_YES_NO_RESPONSES = [QUESTION_RESPONSE.YES, QUESTION_RESPONSE.NO] as const;

/** Valid responses for Yes/No/In process questions (question3) */
export const VALID_YES_NO_IN_PROCESS_RESPONSES = [
  QUESTION_RESPONSE.YES,
  QUESTION_RESPONSE.NO,
  QUESTION_RESPONSE.IN_PROCESS,
] as const;

/**
 * Validates that a name field contains only allowed characters (allowlist approach).
 * Returns error message string if invalid, or true if valid.
 * Compatible with react-hook-form's validate option.
 */
export const validateName = (value: string, errorMessage: string): string | true => {
  if (!value) return true;
  return nameRegex.test(value) ? true : errorMessage;
};

/**
 * Validates that a free-form text field contains only allowed characters (allowlist approach).
 * Returns error message string if invalid, or true if valid.
 * Compatible with react-hook-form's validate option.
 */
export const validateFreeFormText = (value: string, errorMessage: string): string | true => {
  if (!value) return true;
  return freeFormTextRegex.test(value) ? true : errorMessage;
};

/**
 * Validates that a question response is one of the allowed enum values.
 * Returns error message string if invalid, or true if valid.
 * Compatible with react-hook-form's validate option.
 */
export const validateQuestionResponse = (
  value: string,
  allowedValues: readonly string[],
  errorMessage: string
): string | true => {
  if (!value) return true;
  return allowedValues.includes(value) ? true : errorMessage;
};

export const validateMonthYearDateInput = (input: string, output: string) => {
  const inputDate = moment(input, 'MM/YYYY');
  const currentDate = moment();
  return currentDate.isAfter(inputDate, 'month') ? output : undefined;
};

export const relationToAmazonTranslationMap: Record<RelationToAmazon, string> = {
  [RelationToAmazon.SUPPLIER]: FormStrings.SUPPLIER_LABEL,
  [RelationToAmazon.AWS_CUSTOMER]: FormStrings.AWS_CUSTOMER_LABEL,
  [RelationToAmazon.VENDOR]: FormStrings.VENDOR_LABEL,
  [RelationToAmazon.SELLING_PARTNER]: FormStrings.SELLING_PARTNER_LABEL,
  [RelationToAmazon.THE_CLIMATE_PLEDGE_SIGNATORY]: FormStrings.TCP_SIGNATORY_LABEL,
  [RelationToAmazon.AMAZON_CUSTOMER]: FormStrings.AMAZON_CUSTOMER_LABEL,
  [RelationToAmazon.OTHER]: FormStrings.OTHER_LABEL,
};

export const relationToAmazonDescriptionTranslationMap: Record<RelationToAmazon, string> = {
  [RelationToAmazon.SUPPLIER]: FormStrings.SUPPLIER_DESCRIPTION,
  [RelationToAmazon.AWS_CUSTOMER]: FormStrings.AWS_CUSTOMER_DESCRIPTION,
  [RelationToAmazon.VENDOR]: FormStrings.VENDOR_DESCRIPTION,
  [RelationToAmazon.SELLING_PARTNER]: FormStrings.SELLING_PARTNER_DESCRIPTION,
  [RelationToAmazon.THE_CLIMATE_PLEDGE_SIGNATORY]: FormStrings.TCP_SIGNATORY_DESCRIPTION,
  [RelationToAmazon.AMAZON_CUSTOMER]: FormStrings.AMAZON_CUSTOMER_DESCRIPTION,
  [RelationToAmazon.OTHER]: '',
};

/**
 * Single source of truth for relationship delimiter.
 * Includes trailing space to match join output.
 */
export const ORGANIZATION_RELATIONSHIP_DELIMITER = '; ';

export function formatOrganizationRelationship(
  relationships: RelationToAmazon[] | undefined,
  bundle: MessageBundle
): string {
  if (!Array.isArray(relationships) || relationships.length === 0) {
    return '';
  }

  return relationships
    .map((relation) => t(bundle, relationToAmazonTranslationMap[relation], { relation }))
    .join(ORGANIZATION_RELATIONSHIP_DELIMITER);
}

export function parseOrganizationRelationship(formattedString: string, bundle: MessageBundle): RelationToAmazon[] {
  if (!formattedString || formattedString.trim() === '') {
    return [];
  }

  // Split by delimiter and trim whitespace
  const formattedParts = formattedString.split(ORGANIZATION_RELATIONSHIP_DELIMITER).map((part) => part.trim());

  // Create a reverse lookup map from translated strings to enum values
  const reverseTranslationMap: Record<string, RelationToAmazon> = {};
  Object.entries(relationToAmazonTranslationMap).forEach(([enumValue, translationKey]) => {
    const translatedValue = t(bundle, translationKey, { relation: enumValue as RelationToAmazon });
    reverseTranslationMap[translatedValue] = enumValue as RelationToAmazon;
  });

  // Map formatted parts back to enum values
  const relationships: RelationToAmazon[] = [];
  formattedParts.forEach((part) => {
    const enumValue = reverseTranslationMap[part];
    if (enumValue) {
      relationships.push(enumValue);
    }
  });

  return relationships;
}
